import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import { InputField, LabelField, MyCheckbox } from "components";
import Button from "components/button/Button";
import MyFilterButton from "components/MyFilterButton/MyFilterButton";
import { getCurrency, numberFormat } from "helper/numberFormat";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { emailValidation } from "utils/validation";
import { ReactComponent as ClockWiseRefresh } from "../../../assets/icons/clockWise_refresh.svg";
import TributeInformation from "../DonationPay/TributeInformation";
import DonationStepper from "../DonationStepper";
import "./DonationCause.scss";
import { FontWeights, FontColors } from "theme/fontConfig";

const DonationCause = ({
  formik,
  ledgers,
  loading,
  external,
  hasLedgerParams,
}) => {
  const currency = getCurrency();
  const { push } = useHistory();
  const { search } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Get unique ID for a ledger
  const getLedgerId = (ledger) => {
    return ledger?.sub_ledger_id || ledger?.ledger_id || ledger?._id || ledger?.id ||
      (ledger?.sub_ledger_name || ledger?.ledger_name); // fallback to name as ID
  };

  // Get display name for a ledger
  const getLedgerName = (ledger) => {
    return ledger?.sub_ledger_name || ledger?.ledger_name || ledger?.name || ledger?.title;
  };

  // Sort ledgers by name
  const sortedLedgers = useMemo(() => {
    if (!ledgers?.length) return [];
    const getName = (ledger) => ledger?.sub_ledger_name || ledger?.ledger_name || ledger?.name || ledger?.title || "";
    return [...ledgers].sort((a, b) => {
      const nameA = getName(a).toLowerCase();
      const nameB = getName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [ledgers]);

  // Get index of cause in causeInfo array
  const getCauseIndex = (ledger) => {
    const ledgerId = getLedgerId(ledger);
    const ledgerName = getLedgerName(ledger);
    return formik?.values?.causeInfo?.findIndex(
      (item) => {
        const itemId = getLedgerId(item?.cause);
        const itemName = getLedgerName(item?.cause);
        // Match by ID or by name
        return itemId === ledgerId || itemName === ledgerName;
      }
    );
  };

  // Check if a ledger is selected
  const isLedgerSelected = (ledger) => {
    return getCauseIndex(ledger) !== -1;
  };

  // Get cause info for a ledger
  const getCauseInfo = (ledger) => {
    const index = getCauseIndex(ledger);
    return index !== -1 ? formik?.values?.causeInfo?.[index] : null;
  };

  // Toggle ledger selection
  const toggleLedgerSelection = (ledger) => {
    const isSelected = isLedgerSelected(ledger);

    if (isSelected) {
      // Remove from causeInfo
      const indexToRemove = getCauseIndex(ledger);
      const newCauseInfo = formik?.values?.causeInfo?.filter((_, idx) => idx !== indexToRemove);
      formik.setFieldValue("causeInfo", newCauseInfo);
    } else {
      // Add to causeInfo
      const defaultAmount = ledger?.default_amount;
      const newCause = {
        cause: ledger,
        amount: defaultAmount === 0 || defaultAmount == null ? "" : defaultAmount,
        note: "",
        required_note: ledger?.notes || false,
        contribution: { isRecurring: false, frequency: "monthly" },
      };
      formik.setFieldValue("causeInfo", [...(formik?.values?.causeInfo || []), newCause]);
    }
  };

  // Update amount for a ledger
  const updateAmount = (ledger, value) => {
    const index = getCauseIndex(ledger);
    if (index !== -1) {
      formik.setFieldValue(`causeInfo[${index}].amount`, value);
    }
  };

  // Update recurring status
  const updateRecurring = (ledger, isRecurring) => {
    const index = getCauseIndex(ledger);
    if (index !== -1) {
      formik.setFieldValue(`causeInfo[${index}].contribution.isRecurring`, isRecurring);
      if (formik.values?.activePayment === "bill-my-account" && isRecurring) {
        formik.setFieldValue("activePayment", "");
      }
    }
  };

  // Update recurring frequency
  const updateFrequency = (ledger, frequency) => {
    const index = getCauseIndex(ledger);
    if (index !== -1) {
      formik.setFieldValue(`causeInfo[${index}].contribution.frequency`, frequency);
    }
  };

  // Update note
  const updateNote = (ledger, note) => {
    const index = getCauseIndex(ledger);
    if (index !== -1) {
      formik.setFieldValue(`causeInfo[${index}].note`, note);
    }
  };

  useEffect(() => {
    if (!formik?.values?.causeInfo) {
      formik.setFieldValue("causeInfo", []);
    }
  }, []);

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, curr) => acc && acc[curr], obj);
  };

  const displayValidation = (msg) => {
    enqueueSnackbar(msg, { variant: "error" });
  };

  const checkTributeValidation = (values) => {
    const validationRules = [
      { field: "tribute_type.name", message: "Please select tribute type" },
      { field: "donor_name", message: "Please enter donor name" },
    ];
    let valid = true;

    validationRules.some((rule) => {
      const value = getValueByPath(values, rule.field);

      if (!value || (typeof value === "string" && value.trim() === "")) {
        displayValidation(rule.message);
        valid = false;
        return true;
      }

      if (rule.validationFunction && !rule.validationFunction(value)) {
        displayValidation(rule.message);
        valid = false;
        return true;
      }

      return false;
    });

    return valid;
  };

  const checkTributeAcknowledgementValidation = (values) => {
    const validationRules = [
      { field: "recipient_name", message: "Please enter recipient name" },
      {
        field: "email",
        message: "Please enter valid email",
        validationFunction: emailValidation,
      },
    ];
    let valid = true;

    const acknowledgementsCheck = values?.acknowledgements?.some((ack) => {
      validationRules.some((rule) => {
        const value = getValueByPath(ack, rule.field);

        if (!value || (typeof value === "string" && value.trim() === "")) {
          displayValidation(rule.message);
          valid = false;
          return true;
        }
        if (rule.validationFunction && !rule.validationFunction(value)) {
          displayValidation(rule.message);
          valid = false;
          return true;
        }
        return false;
      });

      return !valid;
    });

    return valid;
  };

  const handleNext = () => {
    // Filter out empty cause entries (legacy initialization)
    const validCauses = formik?.values?.causeInfo?.filter(item => item?.cause && item?.cause !== "");

    // Check if at least one cause is selected
    if (!validCauses?.length) {
      return enqueueSnackbar("Please select at least one cause", { variant: "error" });
    }

    // Validate all selected causes
    for (const causeItem of validCauses) {
      const causeName = getLedgerName(causeItem?.cause) || "selected cause";
      const amount = causeItem?.amount;

      if (
        amount === null ||
        amount === undefined ||
        amount === "" ||
        Number(amount) === 0
      ) {
        return enqueueSnackbar(`Please enter amount for ${causeName}`, { variant: "error" });
      }

      if (!causeItem?.note && causeItem?.required_note) {
        return enqueueSnackbar(`Please enter note for ${causeName}`, { variant: "error" });
      }
    }

    // Update causeInfo to only include valid causes before proceeding
    if (validCauses.length !== formik?.values?.causeInfo?.length) {
      formik.setFieldValue("causeInfo", validCauses);
    }

    if (formik?.values?.tributeCheck && !checkTributeValidation(formik?.values))
      return;
    if (
      formik?.values?.tributeCheck &&
      formik?.values?.showAcknowledgement &&
      !checkTributeAcknowledgementValidation(formik?.values)
    )
      return;

    push(
      external
        ? `/donation/details${search}`
        : `/donation-view/details${search}`
    );
  };

  const regex = /([0-9]*[\.|\,]{0,1}[0-9]{0,2})/s;

  return (
    <>
      <div className="donation__content">
        <DonationStepper activeStep={0} />
        <div className="DonationCause__content">
          {/* Single Ledger View (when ledger param is in URL) */}
          {hasLedgerParams ? (
            loading ? (
              <div className="cause-cards-loader">
                <CircularProgress style={{ color: FontColors.Blue2555EF }} />
                <span>Loading Options...</span>
              </div>
            ) : formik?.values?.causeInfo
                ?.filter((item) => item?.cause && item?.cause !== "")
                ?.map((causeItem, index) => {
                  const ledger = causeItem?.cause;
                  const ledgerName = getLedgerName(ledger);
                  const hasPresetAmount = ledger?.default_amount && ledger?.default_amount !== 0;

                  return (
                    <div key={getLedgerId(ledger) || index} className="cause-single">
                      <div className="cause-single__title">{ledgerName}</div>
                      <div className="cause-card__body">
                        <div className="cause-single__amount-row">
                          <div className="cause-single__amount">
                            <InputField
                              onWheel={(e) => e.target.blur()}
                              type="number"
                              step="any"
                              title="Amount"
                              mandatory={true}
                              name={`amount-${getLedgerId(ledger)}`}
                              value={causeItem?.amount ?? ""}
                              placeholder={hasPresetAmount ? "" : "Enter amount"}
                              onKeyPress={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                }
                              }}
                              handleChange={(e) => {
                                const val = e?.target?.value || "";
                                const intPart = val.split(".")[0].replace(/[^0-9]/g, "");
                                if (intPart.length > 10) return;
                                updateAmount(ledger, val.match(regex)[0]);
                              }}
                              placeholderIcon={
                                <span className="cause-card__currency">{currency}</span>
                              }
                              inputClassName="cause-card__amount-input"
                              min="0"
                            />
                          </div>

                          <div className="cause-card__recurring cause-single__recurring">
                            <div className="cause-card__recurring-checkbox">
                              <MyCheckbox
                                handlechange={(e) => updateRecurring(ledger, e.target.checked)}
                                checked={causeItem?.contribution?.isRecurring ?? false}
                                name={`recurring-${getLedgerId(ledger)}`}
                              />
                              <span className="cause-card__recurring-label">Recurring</span>
                            </div>
                            {causeItem?.contribution?.isRecurring && (
                              <MyFilterButton
                                value={
                                  causeItem?.contribution?.frequency === "yearly"
                                    ? "Yearly"
                                    : "Monthly"
                                }
                                items={[
                                  { label: "Monthly", value: "monthly" },
                                  { label: "Yearly", value: "yearly" },
                                ]}
                                name={`frequency-${getLedgerId(ledger)}`}
                                onChange={(v) => updateFrequency(ledger, v)}
                              />
                            )}
                          </div>
                        </div>

                        <div className="cause-card__note">
                          <InputField
                            type="text"
                            title="Note"
                            mandatory={!!ledger?.notes}
                            name={`note-${getLedgerId(ledger)}`}
                            placeholder="Enter note"
                            value={causeItem?.note ?? ""}
                            handleChange={(e) => updateNote(ledger, e?.target?.value)}
                            inputClassName="cause-card__note-input"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
          ) : (
          /* Cause Cards Grid */
          <div className="cause-cards-grid">
            {loading ? (
              <div className="cause-cards-loader">
                <CircularProgress style={{ color: FontColors.Blue2555EF }} />
                <span>Loading Options...</span>
              </div>
            ) : sortedLedgers?.map((ledger, index) => {
              const isSelected = isLedgerSelected(ledger);
              const causeInfo = getCauseInfo(ledger);
              const ledgerName = getLedgerName(ledger);
              const hasPresetAmount = ledger?.default_amount && ledger?.default_amount !== 0;

              return (
                <div
                  key={getLedgerId(ledger) || index}
                  className={`cause-card ${isSelected ? "cause-card--selected" : ""}`}
                >
                  {/* Card Header with Checkbox */}
                  <div className="cause-card__header">
                    <MyCheckbox
                      handlechange={() => toggleLedgerSelection(ledger)}
                      checked={isSelected}
                      name={`cause-${getLedgerId(ledger)}`}
                    />
                    <span className="cause-card__title">{ledgerName}</span>
                  </div>

                  {/* Card Body - Only show when selected */}
                  {isSelected && (
                    <div className="cause-card__body">
                      {/* Amount Input */}
                      <div className="cause-card__amount">
                        <InputField
                          onWheel={(e) => e.target.blur()}
                          type="number"
                          step="any"
                          title="Amount"
                          mandatory={true}
                          name={`amount-${getLedgerId(ledger)}`}
                          value={causeInfo?.amount ?? ""}
                          placeholder={hasPresetAmount ? "" : "Enter amount"}
                          onKeyPress={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                            }
                          }}
                          handleChange={(e) => {
                            const val = e?.target?.value || "";
                            const intPart = val.split(".")[0].replace(/[^0-9]/g, "");
                            if (intPart.length > 10) return;
                            updateAmount(ledger, val.match(regex)[0]);
                          }}
                          placeholderIcon={
                            <span className="cause-card__currency">{currency}</span>
                          }
                          inputClassName="cause-card__amount-input"
                          min="0"
                        />
                      </div>

                      {/* Recurring Options */}
                      <div className="cause-card__recurring">
                        <div className="cause-card__recurring-checkbox">
                          <MyCheckbox
                            handlechange={(e) => updateRecurring(ledger, e.target.checked)}
                            checked={causeInfo?.contribution?.isRecurring ?? false}
                            name={`recurring-${getLedgerId(ledger)}`}
                          />
                          <span className="cause-card__recurring-label">Recurring</span>
                        </div>
                        {causeInfo?.contribution?.isRecurring && (
                          <MyFilterButton
                            value={
                              causeInfo?.contribution?.frequency === "yearly"
                                ? "Yearly"
                                : "Monthly"
                            }
                            items={[
                              { label: "Monthly", value: "monthly" },
                              { label: "Yearly", value: "yearly" },
                            ]}
                            name={`frequency-${getLedgerId(ledger)}`}
                            onChange={(v) => updateFrequency(ledger, v)}
                          />
                        )}
                      </div>

                      {/* Note Field */}
                      <div className="cause-card__note form-group fv-plugins-icon-container">
                        <LabelField
                          htmlFor={`note-${getLedgerId(ledger)}`}
                          title="Note"
                          mandatory={!!ledger?.notes}
                        />
                        <textarea
                          id={`note-${getLedgerId(ledger)}`}
                          className="form-control form-control-solid py-3 px-4 cause-card__note-textarea"
                          placeholder="Enter note"
                          value={causeInfo?.note ?? ""}
                          onChange={(e) => updateNote(ledger, e?.target?.value)}
                          onInput={(e) => {
                            e.target.style.height = "auto";
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          rows={1}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className="summaryText">Summary</div>
            </Grid>
            {formik?.values?.causeInfo
              ?.filter(item => item?.cause && item?.cause !== "")
              ?.map((item, idx) => (
                <Grid item xs={12} key={idx}>
                  <div className="couses__amount d-flex align-items-center justify-content-between">
                    <div>
                      {item?.cause?.sub_ledger_name || item?.cause?.ledger_name}
                    </div>
                    <div className="d-flex align-items-center">
                      {item?.contribution?.isRecurring && (
                        <span className="recurring_value">
                          <ClockWiseRefresh className="mr-1" /> Recurring{" "}
                          {item?.contribution?.frequency}
                        </span>
                      )}
                      {numberFormat({
                        number: +item?.amount,
                        decimal: 2,
                        style: "currency",
                      })}
                    </div>
                  </div>
                </Grid>
              ))}
            <hr className="hr-line" />
            <Grid item xs={12}>
              <div className="total__amount d-flex alien-items-center justify-content-between">
                <div>Total</div>
                <div>
                  {numberFormat({
                    number: +formik?.values?.causeInfo
                      ?.filter(item => item?.cause && item?.cause !== "")
                      ?.reduce((acc, c) => acc + +c?.amount, 0),
                    decimal: 2,
                    style: "currency",
                  })}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
        <div
          style={{
            height: "48px",
            marginTop: "15px",
            width: "100%",
          }}
        >
          <label
            htmlFor="honor"
            className="d-flex align-items-center h-100 checkbox__label"
            style={{ fontWeight: FontWeights.Bold }}
          >
            <MyCheckbox
              handlechange={(e) =>
                formik.setFieldValue("tributeCheck", e?.target?.checked)
              }
              checked={formik?.values?.tributeCheck}
              name="checkboxHonor"
              id="honor"
            />
            Give in honor or memory of someone
          </label>
        </div>
        {formik?.values?.tributeCheck && (
          <div className="tribute__container">
            <div className="tribute_information_text">Tribute Information</div>
            <div className="tribute__content__extDo">
              <TributeInformation formik={formik} />
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end w-100 ">
          <Button
            className="next__btn"
            onClick={handleNext}
            style={{ marginRight: 0, marginLeft: 10 }}
            variant="primary"
            size="md"
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

export default DonationCause;
